import { create } from 'zustand';

/* --------------------------------------------------
   Utilities
-------------------------------------------------- */

const generateId = () =>
  Math.random().toString(36).substring(2, 15);

/**
 * Returns a user-scoped localStorage key.
 * Falls back to 'guest' if user_id is not in sessionStorage yet.
 */
const userKey = (key) => {
  const uid =
    sessionStorage.getItem('user_id') || 'guest';
  return `nitor_${uid}_${key}`;
};

const loadState = () => {
  try {
    const role =
      localStorage.getItem(userKey('role')) ||
      'pre_sales';

    const messages = JSON.parse(
      localStorage.getItem(userKey('messages')) ||
      '{"pre_sales":[],"marketing":[]}'
    );

    const history = JSON.parse(
      localStorage.getItem(userKey('history')) || '[]'
    );

    const sessionId =
      localStorage.getItem(userKey('session_id'));

    // sessionMessages: { [sessionId]: { pre_sales: [], marketing: [] } }
    const sessionMessages = JSON.parse(
      localStorage.getItem(userKey('session_messages')) || '{}'
    );

    return { role, messages, history, sessionId, sessionMessages };
  } catch {
    return {
      role: 'pre_sales',
      messages: { pre_sales: [], marketing: [] },
      history: [],
      sessionId: null,
      sessionMessages: {},
    };
  }
};

const initial = loadState();

/* --------------------------------------------------
   Zustand Store
-------------------------------------------------- */

export const useChatStore = create((set, get) => ({
  // ---- State -------------------------------------------------
  activeRole: initial.role,
  messages: initial.messages,
  history: initial.history,
  activeSessionId: initial.sessionId,
  sessionMessages: initial.sessionMessages,
  isThinking: false,
  sidebarOpen: false,

  // ---- Actions -----------------------------------------------
  setRole: (role) => {
    localStorage.setItem(userKey('role'), role);
    set({ activeRole: role });
  },

  setSessionId: (id) => {
    localStorage.setItem(userKey('session_id'), id);
    set({ activeSessionId: id });
  },

  addMessage: (msg) => {
    const {
      activeRole,
      messages,
      history,
      activeSessionId,
    } = get();

    const roleMessages = [
      ...messages[activeRole],
      msg,
    ];

    const newMessages = {
      ...messages,
      [activeRole]: roleMessages,
    };

    let newHistory = [...history];
    let { sessionMessages } = get();
    let newSessionMessages = { ...sessionMessages };
    let currentSessionId = activeSessionId;

    // Create a new chat session on first user message
    if (msg.role === 'user') {
      if (
        !activeSessionId ||
        roleMessages.length === 1
      ) {
        const sessionId = generateId();
        currentSessionId = sessionId;

        newHistory.unshift({
          id: sessionId,
          title: msg.content.substring(0, 50),
          timestamp: new Date(),
          role: activeRole,
          userId: sessionStorage.getItem('user_id') || 'guest',
        });

        localStorage.setItem(
          userKey('session_id'),
          sessionId
        );

        set({ activeSessionId: sessionId });
      }
    }

    // Snapshot messages for this session
    if (currentSessionId) {
      newSessionMessages[currentSessionId] = {
        ...newMessages,
      };
      localStorage.setItem(
        userKey('session_messages'),
        JSON.stringify(newSessionMessages)
      );
    }

    localStorage.setItem(
      userKey('messages'),
      JSON.stringify(newMessages)
    );
    localStorage.setItem(
      userKey('history'),
      JSON.stringify(newHistory)
    );

    set({
      messages: newMessages,
      history: newHistory,
      sessionMessages: newSessionMessages,
    });
  },

  setThinking: (v) => set({ isThinking: v }),

  setSidebarOpen: (v) =>
    set({ sidebarOpen: v }),

  newChat: () => {
    const { activeRole, messages } = get();

    const newMessages = {
      ...messages,
      [activeRole]: [],
    };

    localStorage.setItem(
      userKey('messages'),
      JSON.stringify(newMessages)
    );
    localStorage.removeItem(userKey('session_id'));

    set({
      messages: newMessages,
      activeSessionId: null,
    });
  },

  loadSession: (sessionId) => {
    const { sessionMessages, history } = get();
    const saved = sessionMessages[sessionId];
    if (!saved) return;

    const historyEntry = history.find((h) => h.id === sessionId);
    const role = historyEntry?.role || 'pre_sales';

    localStorage.setItem(userKey('session_id'), sessionId);
    localStorage.setItem(userKey('role'), role);
    localStorage.setItem(userKey('messages'), JSON.stringify(saved));

    set({
      activeSessionId: sessionId,
      activeRole: role,
      messages: saved,
    });
  },

  setFeedback: (msgId, feedback) => {
    const { activeRole, messages } = get();

    const roleMessages = messages[
      activeRole
    ].map((m) =>
      m.id === msgId
        ? {
          ...m,
          feedback,
          feedbackSubmitted: true,
        }
        : m
    );

    const newMessages = {
      ...messages,
      [activeRole]: roleMessages,
    };

    localStorage.setItem(
      userKey('messages'),
      JSON.stringify(newMessages)
    );

    set({ messages: newMessages });
  },

  clearMessages: () => {
    const newMessages = {
      pre_sales: [],
      marketing: [],
    };

    localStorage.setItem(
      userKey('messages'),
      JSON.stringify(newMessages)
    );

    set({ messages: newMessages });
  },

  // ---- Selectors / Helpers -----------------------------------
  getChatHistoryForApi: (n = 4) => {
    const { activeRole, messages } = get();
    const roleMessages = messages[activeRole];

    const pairs = [];

    for (let i = 0; i < roleMessages.length - 1; i++) {
      const m = roleMessages[i];
      const next = roleMessages[i + 1];

      if (
        m.role === 'user' &&
        next.role === 'assistant'
      ) {
        pairs.push({
          query: m.content,
          answer: next.content,
        });
        i++; // skip assistant message
      }
    }

    return pairs.slice(-n);
  },
}));